import {
  Autocomplete,
  Button,
  Chip,
  FormControl,
  FormHelperText,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";

import { Box } from "@mui/system";

const InputStyle = {
  marginBottom: 2,
};

// #91 - the two carrier types, with the explainer wording shown above the
// picker (wording confirmed by the client). Each type renders as a tinted
// pill, in the legend up top and in the table's Type column.
const CARRIER_TYPES = ["Health", "Life"];
const TYPE_EXPLAINERS = {
  Life: "Life, Annuity, Long Term Care, and more",
  Health: "Medicare Advantage, PDP, ACA, and Medicare Supplement",
};
const TYPE_CHIP_STYLES = {
  Health: { bgcolor: "#E3F2FD", color: "#1565C0" },
  Life: { bgcolor: "#E8F5E9", color: "#2E7D32" },
};
const TypeChip = ({ type }) =>
  type ? (
    <Chip
      label={type}
      size="small"
      sx={{ fontWeight: 600, ...TYPE_CHIP_STYLES[type] }}
    />
  ) : null;

const CarrierSelection = ({
  carrierList,
  updateOptions,
  carriers,
  setCarriers,
  showPolicyEntity = false,
  fieldErrors = {},
}) => {
  const [inputValue, setInputValue] = useState("");
  const [value, setValue] = useState(null);
  // #91 - carriers are picked type-first. The chosen type sticks after an add
  // so several same-type carriers can be added without re-picking it.
  const [addType, setAddType] = useState("");

  const unselectedCarriers = (carrierList || [])
    .filter((carrier) => !carrier.isInactive)
    .filter((carrier) => !carriers.some((c) => c.value === carrier.id));
  const addableCarriers = unselectedCarriers.filter(
    (carrier) => carrier.productType === addType
  );

  function push(carrier) {
    setCarriers([...carriers, carrier]);
  }

  function remove(c) {
    setCarriers(carriers.filter((carrier) => carrier.value !== c.id));
  }

  if (!carrierList) return <></>;

  const columnCount = showPolicyEntity ? 7 : 6;

  // #91 - the repeating add template: a Type dropdown gating a Carrier
  // dropdown. Rendered standalone ("Choose a carrier") when nothing is
  // selected yet, and as the table's final row ("Add another") after that.
  const addControls = (heading) => (
    <>
      <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
        {heading}
      </Typography>
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        <Select
          value={addType}
          displayEmpty={true}
          onChange={(event) => setAddType(event.target.value)}
          sx={{ minWidth: 140, bgcolor: "background.paper" }}
          SelectDisplayProps={{ "aria-label": "Carrier type" }}
        >
          <MenuItem value="">
            <em>Type</em>
          </MenuItem>
          {CARRIER_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </Select>
        <Autocomplete
          disablePortal
          fullWidth={true}
          disabled={!addType}
          sx={{ bgcolor: "background.paper" }}
          onChange={(event, carrier) => {
            if (!carrier) {
              return;
            }
            push({
              value: carrier.id,
              policyEntity: "",
              policyCreation: "",
              // Left unset so "Transfer" requires an explicit Yes/No answer.
              withRelease: undefined,
              states: [],
            });
            setInputValue("");
            setValue(null);
          }}
          inputValue={inputValue}
          value={value}
          getOptionLabel={(option) => option.value}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === "reset" || reason === "clear") {
              setInputValue("");
              return;
            }
            setInputValue(newInputValue);
          }}
          options={addableCarriers}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth={true}
              label="Carrier"
              helperText={addType ? undefined : "Choose a type first"}
            />
          )}
        />
      </Box>
    </>
  );

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, marginBottom: 2 }}>
        {["Life", "Health"].map((t) => (
          <Box key={t} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TypeChip type={t} />
            <Typography variant="body2">{TYPE_EXPLAINERS[t]}</Typography>
          </Box>
        ))}
      </Box>
      {carriers.length === 0 ? (
        <Paper sx={{ padding: 2, marginBottom: 3 }}>
          {addControls("Choose a carrier")}
        </Paper>
      ) : (
        <TableContainer sx={{ marginBottom: 3 }} component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="Carrier selection">
            <TableHead>
              <TableRow>
                <TableCell>Carrier</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">New/Transfer *</TableCell>
                <TableCell>State</TableCell>
                {showPolicyEntity && (
                  <TableCell align="right">Individual/Corp *</TableCell>
                )}
                <TableCell align="right">Do you have a release letter?</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {carriers.map((row, index) => {
                let carrier = {};
                carrierList.forEach((c) => {
                  if (c.id === row.value) carrier = c;
                });
                // #34 — per-row validation messages, keyed by field.
                const err = fieldErrors[index] || {};

                return (
                  <TableRow key={carrier.id}>
                    <TableCell scope="row">{carrier.value}</TableCell>
                    <TableCell>
                      <TypeChip type={carrier.productType} />
                    </TableCell>
                    <TableCell align="right">
                      <FormControl error={Boolean(err.policyCreation)}>
                        <Select
                          value={row.policyCreation}
                          displayEmpty={true}
                          onChange={(event) => {
                            updateOptions("policyCreation", row, event);
                          }}
                          sx={{ ...InputStyle, minWidth: 150 }}
                        >
                          <MenuItem value="">
                            <em>Choose one...</em>
                          </MenuItem>
                          <MenuItem value={"new"}>New</MenuItem>
                          <MenuItem value={"transfer"}>Transfer</MenuItem>
                        </Select>
                        {err.policyCreation && (
                          <FormHelperText>Required</FormHelperText>
                        )}
                      </FormControl>
                    </TableCell>
                    <TableCell align="left">
                      {carrier.states?.length ? (
                        <FormControl error={Boolean(err.states)}>
                          <Select
                            value={row.states}
                            displayEmpty={true}
                            renderValue={(val) => {
                              if (!val) return <em>Select states</em>;
                              return val.join(", ");
                            }}
                            onChange={(event) => {
                              updateOptions("states", row, event);
                            }}
                            sx={{ ...InputStyle, minWidth: 150 }}
                            required={true}
                            multiple={true}
                          >
                            {carrier.states.map((state) => {
                              return (
                                <MenuItem key={state} value={state}>
                                  {state}
                                </MenuItem>
                              );
                            })}
                          </Select>
                          {err.states && (
                            <FormHelperText>Required</FormHelperText>
                          )}
                        </FormControl>
                      ) : null}
                    </TableCell>
                    {showPolicyEntity && (
                      <TableCell align="right">
                        <FormControl error={Boolean(err.policyEntity)}>
                          <Select
                            displayEmpty={true}
                            value={row.policyEntity}
                            onChange={(event) => {
                              updateOptions("policyEntity", row, event);
                            }}
                            sx={{ ...InputStyle, minWidth: 150 }}
                          >
                            <MenuItem value="">
                              <em>Individual/Corp</em>
                            </MenuItem>
                            <MenuItem value={"individual"}>Individual</MenuItem>
                            <MenuItem value={"corporate"}>Corporate</MenuItem>
                          </Select>
                          {err.policyEntity && (
                            <FormHelperText>Required</FormHelperText>
                          )}
                        </FormControl>
                      </TableCell>
                    )}
                    <TableCell align="right">
                      {row.policyCreation === "transfer" && (
                        <FormControl error={Boolean(err.withRelease)}>
                          <Select
                            value={
                              row.withRelease === true
                                ? "yes"
                                : row.withRelease === false
                                ? "no"
                                : ""
                            }
                            displayEmpty={true}
                            onChange={(event) => {
                              const v = event.target.value;
                              updateOptions("withRelease", row, {
                                target: {
                                  value:
                                    v === "yes" ? true : v === "no" ? false : undefined,
                                },
                              });
                            }}
                            sx={{ ...InputStyle, minWidth: 120 }}
                          >
                            <MenuItem value="">
                              <em>Choose one...</em>
                            </MenuItem>
                            <MenuItem value={"yes"}>Yes</MenuItem>
                            <MenuItem value={"no"}>No</MenuItem>
                          </Select>
                          {err.withRelease && (
                            <FormHelperText>Required</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        onClick={() => {
                          remove(carrier);
                        }}
                      >
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow sx={{ backgroundColor: "action.hover" }}>
                <TableCell
                  colSpan={columnCount}
                  sx={{ borderBottom: "none" }}
                >
                  {addControls("To add another, choose a carrier.")}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default CarrierSelection;
